"use client";

import { LISTING_GROUPS, listingGroup, compareProfileListings, type ListingGroup } from "@/lib/profile-listing-groups";
import { useState } from "react";

import type { ProfileListing } from "./profile-types";
import { ListingCard } from "./listing-card";
import { ProfilePagination } from "./profile-pagination";
import { DirectoryFilterTelemetry } from "@/components/analytics/DirectoryFilterTelemetry";

/** Four source-derived transaction groups, six cards per page.
 * Sort the whole selected group before slicing; every tab resets pagination.
 * Unsupported types remain unclassified rather than implying a sale or lease.
 */

const PAGE_SIZE = 6;

export function ProfileListings({
  listings,
  basePath,
  orgIdentifier,
  agentId,
}: {
  listings: ProfileListing[];
  basePath: string;
  orgIdentifier?: string;
  agentId?: string;
}) {
  const [tab, setTab] = useState<ListingGroup>("sale");
  const [page, setPage] = useState(1);

  const tabs = LISTING_GROUPS.map((group) => ({ ...group,
    count: listings.filter((listing) => listingGroup(listing) === group.key).length,
  }));
  const filtered = listings.filter((listing) => listingGroup(listing) === tab).sort(compareProfileListings);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <section>
      <DirectoryFilterTelemetry orgIdentifier={orgIdentifier} surface="agent_listings" routeTemplate="/agents/[slug]"
        activeFilters={{ status: tab }} resultCount={filtered.length} agentId={agentId} />
      <h2
        className="mb-4 text-xl font-semibold"
        style={{
          fontFamily: "var(--brand-font-heading)",
          color: "var(--brand-text)",
        }}
      >
        Listings
      </h2>

      <>
          {/* Status-filter pill tabs */}
          <div className="mb-5 flex flex-wrap gap-2">
            {tabs.map((t) => {
              const selected = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setTab(t.key);
                    setPage(1);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold"
                  style={
                    selected
                      ? {
                          backgroundColor: "var(--brand-primary)",
                          color: "var(--brand-primary-foreground)",
                        }
                      : {
                          backgroundColor: "var(--brand-surface-subtle)",
                          color: "var(--brand-text-muted)",
                        }
                  }
                >
                  {t.label}{" "}
                  <span
                    className="rounded-full px-1.5 text-[11px] font-bold"
                    style={
                      selected
                        ? {
                            backgroundColor:
                              "color-mix(in srgb, var(--brand-primary-foreground) 22%, transparent)",
                            color: "inherit",
                          }
                        : {
                            backgroundColor:
                              "color-mix(in srgb, var(--brand-text) 10%, transparent)",
                          }
                    }
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && <p className="rounded-lg border p-6 text-sm" style={{ borderColor: "var(--brand-card-border)", color: "var(--brand-text-muted)" }}>
            {listings.length === 0 ? "No listings yet" : "No properties in this group"}
          </p>}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {pageItems.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                basePath={basePath}
              />
            ))}
          </div>

          <ProfilePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            ariaLabel="Listings pages"
          />
      </>
    </section>
  );
}
